import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Progress } from 'flowbite-react';
import { BriefingSkeleton, Icon } from '..';
import type { IconType } from '../Icon';
import { currentSprint, useAppDispatch, useAppSelector } from '../../redux';

interface DashboardBriefingProps {
    statistics?: {
        changes?: {
            status?: Record<string, number>;
            total?: number;
        };
    };
    showSprintInfo?: boolean;
    showKpis?: boolean;
    hiddenKpis?: string[];
}

type KpiDef = {
    key: string;
    icon: IconType;
    href: string;
    ability?: string;
};

const overviewLink = (status: string) => `/changes?status=${status}`;

const DEV_KPIS: KpiDef[] = [
    { key: 'inProgress', icon: 'commit', href: overviewLink('inProgress') },
    { key: 'pendingEstimate', icon: 'estimated', href: overviewLink('pendingEstimate'), ability: 'change:estimate' },
    { key: 'pendingTriage', icon: 'task', href: overviewLink('pendingTriage') },
    { key: 'teamReview', icon: 'eye', href: overviewLink('teamReview') },
];

const CLIENT_KPIS: KpiDef[] = [
    { key: 'clientReview', icon: 'eye', href: overviewLink('clientReview') },
    { key: 'inProgress', icon: 'commit', href: overviewLink('inProgress') },
];

function DashboardBriefing(props: DashboardBriefingProps) {
    const {
        statistics,
        showSprintInfo = true,
        showKpis = true,
        hiddenKpis = [],
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { user } = useAppSelector(state => state.auth);
    const { current } = useAppSelector(state => state.sprints);
    const { loadingOne } = useAppSelector(state => state.statistics || {});

    const canSeeSprint = (user?.can('sprint:read') ?? false) && showSprintInfo;
    const isClient = user?.role === 'client';
    const isLoadingStats = showKpis && !statistics && (loadingOne === 'pending' || loadingOne === 'idle');

    React.useEffect(() => {
        if (canSeeSprint) {
            dispatch(currentSprint());
        }
    }, [dispatch, canSeeSprint]);

    if (isLoadingStats) {
        return <BriefingSkeleton />;
    }

    const userId = user?._id;
    const estimated = current?.getEstimatedHours(null, userId) ?? 0;
    const realised = current?.getRealisedHours(moment(), null, userId) ?? 0;
    const remaining = current?.getRemainingHours(moment(), null, userId) ?? 0;
    const sprintProgress = estimated > 0 ? Math.min(100, Math.round((realised / estimated) * 100)) : 0;

    const statusCounts = statistics?.changes?.status ?? {};
    const hidden = new Set(hiddenKpis);
    const kpiDefs = showKpis
        ? (isClient ? CLIENT_KPIS : DEV_KPIS)
            .filter((kpi) => !kpi.ability || user?.can(kpi.ability))
            .filter((kpi) => !hidden.has(kpi.key))
        : [];

    return (
        <div className="border-b bg-white px-4 py-4 dark:bg-gray-900 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t('dashboard.greeting', { name: user?.firstname ?? '' })}
                    </h2>
                    {canSeeSprint && current?.name && (
                        <>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {t('dashboard.current_sprint')}&nbsp;—&nbsp;{current.name}
                                {remaining > 0 && (
                                    <span className="ml-2">
                                        · {remaining.toFixed(1)}{t('changes.estimate_units_min')} {t('dashboard.remaining')}
                                    </span>
                                )}
                            </p>
                            {estimated > 0 && (
                                <div className="mt-2 max-w-md">
                                    <Progress
                                        progress={sprintProgress}
                                        size="sm"
                                        color={sprintProgress >= 100 ? 'green' : 'blue'}
                                    />
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        {t('dashboard.sprint_progress', {
                                            progress: sprintProgress,
                                            realised: realised.toFixed(1),
                                            estimated: estimated.toFixed(1),
                                        })}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {kpiDefs.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {kpiDefs.map((kpi) => {
                            const count = statusCounts[kpi.key] ?? 0;
                            if (count === 0) {
                                return null;
                            }
                            return (
                                <Link
                                    key={kpi.key}
                                    to={kpi.href}
                                    className="group rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 no-underline transition hover:border-indigo-300 hover:bg-indigo-500 hover:no-underline dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                                >
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 group-hover:text-white dark:text-gray-400">
                                        <Icon type={kpi.icon} size={4} color="current" />
                                        {t(`changes.progress_status_type.${kpi.key}`)}
                                    </div>
                                    <div className="mt-0.5 text-2xl font-bold text-gray-900 group-hover:text-white dark:text-white">
                                        {count}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default DashboardBriefing;
